import { useState } from 'react';
import { Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ProjectContactFormProps {
  projectId: string;
  /** Human-readable reference code (e.g. ET-PJ000012). Falls back to UUID slice. */
  referenceCode?: string;
  onSuccess?: () => void;
}

export function ProjectContactForm({ projectId, referenceCode, onSuccess }: ProjectContactFormProps) {
  const refId = (referenceCode || `ID${projectId.slice(0, 8)}`).toUpperCase();
  const defaultMsg = `I would like to inquire about project EthioRealEstates - ${refId}. Please contact me at your earliest convenience.`;
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: defaultMsg });
  const [role, setRole] = useState('buyer');
  const [keepInformed, setKeepInformed] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Please fill in your name and phone number');
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.from('leads').insert({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        message: form.message.trim() || null,
        project_id: projectId,
        source: 'project_page',
      });
      if (error) throw error;
      toast.success('Your inquiry has been sent!');
      setForm({ name: '', phone: '', email: '', message: defaultMsg });
      onSuccess?.();
    } catch {
      toast.error('Failed to send inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <Label className="text-xs font-medium mb-1 block">Your Name <span className="text-destructive">*</span></Label>
        <Input placeholder="How should we address you?" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
      </div>
      <div>
        <Label className="text-xs font-medium mb-1 block">Phone Number <span className="text-destructive">*</span></Label>
        <div className="flex gap-2">
          <div className="flex items-center gap-1 border rounded-md px-2 text-sm text-muted-foreground shrink-0">🇪🇹 +251</div>
          <Input placeholder="Phone number" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} required />
        </div>
      </div>
      <div>
        <Label className="text-xs font-medium mb-1 block">Email Address</Label>
        <Input placeholder="Your best email address?" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
      </div>
      <div>
        <Label className="text-xs font-medium mb-1 block">Message</Label>
        <Textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} rows={3} />
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-medium">I am a:</Label>
        <RadioGroup value={role} onValueChange={setRole} className="flex gap-4">
          {[
            { value: 'buyer', label: 'Buyer / Tenant' },
            { value: 'agent', label: 'Agent' },
            { value: 'other', label: 'Other' },
          ].map((opt) => (
            <div key={opt.value} className="flex items-center gap-1.5">
              <RadioGroupItem value={opt.value} id={`pcf-role-${opt.value}`} />
              <Label htmlFor={`pcf-role-${opt.value}`} className="text-xs cursor-pointer">{opt.label}</Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      <div className="flex items-center gap-2">
        <Checkbox id="pcf-keep-informed" checked={keepInformed} onCheckedChange={(c) => setKeepInformed(!!c)} />
        <Label htmlFor="pcf-keep-informed" className="text-xs cursor-pointer">Keep me informed about similar properties.</Label>
      </div>

      <Button
        type="submit"
        variant="outline"
        className="w-full h-11 font-bold text-primary border-primary/30 bg-primary/5 hover:bg-primary/10 gap-2"
        disabled={submitting}
      >
        <Mail className="h-4 w-4" />
        {submitting ? 'Sending...' : 'SEND EMAIL'}
      </Button>
      <p className="text-[10px] text-muted-foreground text-center">
        By submitting this form, you agree to our <span className="text-primary cursor-pointer">Terms of Use</span>.
      </p>
    </form>
  );
}
